import { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Briefcase, ChevronDown, ChevronRight, Wand2 } from 'lucide-react';
import { getPermissionForPath } from '@/config/routePermissions';
import { cn } from '@/lib/cn';
import { useSetupDrafts } from '@/features/setup-wizard/hooks/useSetupWizard';
import { draftSetupPath, draftTitle, sortDraftsByRecency } from '@/features/setup-wizard/utils/setupDraft';
import { useCompanyNavList } from '../hooks/useCompanyNavList';
import type { Company } from '@/types/company.types';

const MAX_INLINE_COMPANIES = 6;

export interface SidebarCompaniesSectionProps {
  showLabels: boolean;
  can: (module: string, action: string) => boolean;
  permissionsLoading: boolean;
  baseLinkClass: string;
  activeClass: string;
  inactiveClass: string;
  childLinkClass: string;
  childInactiveClass: string;
  childActiveClass: string;
}

function getActiveCompanyId(pathname: string): string | null {
  const match = pathname.match(/^\/companies\/([^/]+)/);
  if (!match) return null;
  const id = match[1];
  if (id === 'new' || id === 'setup-wizard') return null;
  return id;
}

export function SidebarCompaniesSection({
  showLabels,
  can,
  permissionsLoading,
  baseLinkClass,
  activeClass,
  inactiveClass,
  childLinkClass,
  childInactiveClass,
  childActiveClass,
}: SidebarCompaniesSectionProps): React.ReactElement | null {
  const { pathname } = useLocation();
  const permission = getPermissionForPath('/companies');
  const enabled = !permission || (!permissionsLoading && can(permission.module, 'read'));

  const { data: companies = [], isLoading } = useCompanyNavList();
  const canStartCompanySetup =
    !permissionsLoading &&
    can('companies', 'create') &&
    can('import_setup', 'read') &&
    can('import_setup', 'update');
  const { data: setupDrafts = [] } = useSetupDrafts({ enabled: canStartCompanySetup });
  const drafts = useMemo(() => sortDraftsByRecency(setupDrafts), [setupDrafts]);
  const [expanded, setExpanded] = useState(() => pathname.startsWith('/companies'));

  useEffect(() => {
    if (pathname.startsWith('/companies')) setExpanded(true);
  }, [pathname]);

  const visibleCompanies = useMemo<Company[]>(() => {
    const head = companies.slice(0, MAX_INLINE_COMPANIES);
    const activeId = getActiveCompanyId(pathname);
    if (!activeId) return head;
    if (head.some((c) => c._id === activeId)) return head;
    const active = companies.find((c) => c._id === activeId);
    if (!active) return head;
    return [active, ...head.slice(0, MAX_INLINE_COMPANIES - 1)];
  }, [companies, pathname]);

  const overflowCount = Math.max(companies.length - MAX_INLINE_COMPANIES, 0);

  if (!enabled) return null;

  if (!showLabels) {
    return (
      <NavLink
        to="/companies"
        className={({ isActive, isPending }) =>
          cn(
            baseLinkClass,
            'relative',
            isActive || isPending || pathname.startsWith('/companies') ? activeClass : inactiveClass
          )
        }
        title={drafts.length > 0 ? 'Companies — setup in progress' : 'Companies'}
        end={false}
      >
        <Briefcase className="h-5 w-5 shrink-0" aria-hidden />
        {drafts.length > 0 ? (
          <span
            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:bg-amber-400 dark:ring-gray-800"
            aria-hidden
          />
        ) : null}
      </NavLink>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1">
        <NavLink
          to="/companies"
          className={({ isActive, isPending }) =>
            cn(
              baseLinkClass,
              'min-w-0 flex-1',
              isActive || isPending || pathname.startsWith('/companies')
                ? activeClass
                : inactiveClass
            )
          }
          end={false}
        >
          <Briefcase className="h-5 w-5 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap text-left">
            Companies
          </span>
        </NavLink>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className={cn(
            'shrink-0 rounded-xl p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
            pathname.startsWith('/companies')
              ? 'text-primary hover:bg-primary/10 dark:text-blue-300 dark:hover:bg-primary/20'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200'
          )}
          aria-label={expanded ? 'Collapse companies submenu' : 'Expand companies submenu'}
          aria-expanded={expanded}
          aria-controls="companies-submenu"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
          )}
        </button>
      </div>
      {expanded && (
        <div
          id="companies-submenu"
          className="mt-1 space-y-1 border-l border-gray-200 pl-2 ml-4 dark:border-gray-600"
        >
          {drafts.map((draft) => (
            <NavLink
              key={draft._id}
              to={draftSetupPath(draft)}
              className={({ isPending }) =>
                cn(
                  childLinkClass,
                  'gap-1.5',
                  isPending || pathname.includes(`/drafts/${draft._id}`)
                    ? childActiveClass
                    : 'text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-400/10'
                )
              }
              title={`Continue setup — ${draftTitle(draft)}`}
            >
              <Wand2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{draftTitle(draft)}</span>
              <span className="shrink-0 rounded bg-amber-100 px-1 py-px text-[10px] font-medium text-amber-900 dark:bg-amber-400/15 dark:text-amber-300">
                Draft
              </span>
            </NavLink>
          ))}
          {isLoading ? (
            <div className="py-2 pl-2 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
          ) : visibleCompanies.length === 0 ? (
            drafts.length > 0 ? null : (
              <div className="py-2 pl-2 text-xs text-gray-500 dark:text-gray-400">
                No companies yet
              </div>
            )
          ) : (
            <>
              {visibleCompanies.map((c) => (
                <NavLink
                  key={c._id}
                  to={`/companies/${c._id}`}
                  className={({ isPending }) =>
                    cn(
                      childLinkClass,
                      isPending || pathname.startsWith(`/companies/${c._id}`)
                        ? childActiveClass
                        : childInactiveClass
                    )
                  }
                >
                  <span className="min-w-0 flex-1 truncate" title={c.name}>
                    {c.name}
                  </span>
                </NavLink>
              ))}
              {overflowCount > 0 && (
                <NavLink
                  to="/companies"
                  end
                  className={({ isActive }) =>
                    cn(
                      childLinkClass,
                      'text-xs italic',
                      isActive ? childActiveClass : childInactiveClass
                    )
                  }
                >
                  <span className="min-w-0 flex-1 truncate">View all ({companies.length})</span>
                </NavLink>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

SidebarCompaniesSection.displayName = 'SidebarCompaniesSection';
